import axios from 'axios';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ReviewRequestStep } from './ReviewRequestStep';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';

const WorkflowModal = ({ pet, onClose, onSuccess, openSMSModal, openEmailModal, openCertificateModal }) => {
  const [workflowData, setWorkflowData] = useState({
    booking_confirmation_sms: pet.booking_confirmation_sms || false,
    home_pickup_delivery_sms: pet.home_pickup_delivery_sms || false,
    euthanasia_support_sms: pet.euthanasia_support_sms || false,
    pet_collected_by_driver: pet.pet_collected_by_driver || false,
    pet_in_care_sms: pet.pet_in_care_sms || false,
    aquacremation_sms: pet.aquacremation_sms || false,
    aquacremation_sms_date: formatDatetimeForInput(pet.aquacremation_sms_date),
    tree_planting_email: pet.tree_planting_email || false,
    tree_planting_option: pet.tree_planting_option || 'OTP',
    tree_planting_reply_email: pet.tree_planting_reply_email || false,
    pickup_ashes_sms: pet.pickup_ashes_sms || false,
    ashes_collection_booked: pet.ashes_collection_booked || false,
    ashes_collection_datetime: formatDatetimeForInput(pet.ashes_collection_datetime),
    certificate_email: pet.certificate_email || false,
    review_sms: pet.review_sms || false
  });
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showKeepsakeConfirmation, setShowKeepsakeConfirmation] = useState(false);
  const [keepsakeData, setKeepsakeData] = useState(null);
  const [scheduledSMS, setScheduledSMS] = useState([]);
  const completeReview = useCallback(() => setWorkflowData(prev => prev.review_sms ? prev : { ...prev, review_sms: true }), []);
  
  // Fetch scheduled SMS for this pet
  const fetchScheduledSMS = async () => {
    try {
      const response = await apiClient.get(`/sms/scheduled/${pet.id}`);
      setScheduledSMS(response.data.scheduled_sms || []);
      console.log('📅 Fetched scheduled SMS:', response.data.scheduled_sms);
    } catch (error) {
      console.error('Failed to fetch scheduled SMS:', error);
    }
  };
  
  useEffect(() => {
    fetchScheduledSMS();
    
    // Refresh every 10 seconds (reduced from 30) to catch updates faster
    const interval = setInterval(fetchScheduledSMS, 10000);
    return () => clearInterval(interval);
  }, [pet.id]);
  
  // Helper function to get scheduled SMS for a specific step
  const getScheduledSMSForStep = (stepKey) => {
    return scheduledSMS.find(sms => sms.step_key === stepKey);
  };
  
  // Helper function to cancel scheduled SMS
  const cancelScheduledSMS = async (communicationId, stepKey) => {
    try {
      await apiClient.delete(`/sms/scheduled/${communicationId}`);
      showToast('Scheduled SMS canceled', 'info');
      // Refresh scheduled SMS list
      const response = await apiClient.get(`/sms/scheduled/${pet.id}`);
      setScheduledSMS(response.data.scheduled_sms || []);
    } catch (error) {
      console.error('Failed to cancel scheduled SMS:', error);
      showToast('Failed to cancel scheduled SMS', 'error');
    }
  };
  
  // Update workflowData when pet prop changes (e.g., after SMS sent)
  useEffect(() => {
    setWorkflowData({
      booking_confirmation_sms: pet.booking_confirmation_sms || false,
      home_pickup_delivery_sms: pet.home_pickup_delivery_sms || false,
      euthanasia_support_sms: pet.euthanasia_support_sms || false,
      pet_collected_by_driver: pet.pet_collected_by_driver || false,
      pet_in_care_sms: pet.pet_in_care_sms || false,
      aquacremation_sms: pet.aquacremation_sms || false,
      aquacremation_sms_date: formatDatetimeForInput(pet.aquacremation_sms_date),
      tree_planting_email: pet.tree_planting_email || false,
      tree_planting_option: pet.tree_planting_option || 'OTP',
      tree_planting_reply_email: pet.tree_planting_reply_email || false,
      pickup_ashes_sms: pet.pickup_ashes_sms || false,
      ashes_collection_booked: pet.ashes_collection_booked || false,
      ashes_collection_datetime: formatDatetimeForInput(pet.ashes_collection_datetime),
      certificate_email: pet.certificate_email || false,
      review_sms: pet.review_sms || false
    });
  }, [pet]);

  const handleKeepsakeCancelled = () => {
    setShowKeepsakeConfirmation(false);
    setKeepsakeData(null);
  };

  const handleKeepsakeConfirmed = () => {
    setShowKeepsakeConfirmation(false);
    setKeepsakeData(null);
    // Continue with the workflow update
    // This would be where you'd proceed with the location transfer logic
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveError('');
    
    console.log('🔄 Workflow update started');
    console.log('📝 Pet ID:', pet?.id);
    console.log('📝 Workflow data:', workflowData);

    try {
      const submitData = {
        ...workflowData,
        aquacremation_sms_date: workflowData.aquacremation_sms_date || null,
        ashes_collection_datetime: workflowData.ashes_collection_datetime || null
      };
      
      console.log('📤 Submitting workflow data:', submitData);

      await apiClient.put(`/pets/${pet.id}/workflow`, submitData);
      console.log('✅ Workflow update successful');
      showToast('Workflow updated successfully! ✅');
      onSuccess();
    } catch (error) {
      console.error('❌ Failed to update workflow:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Failed to update workflow. Please try again.';
      setSaveError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:p-4 z-[55]" data-testid="workflow-update-modal">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col relative">
        <div className="p-4 sm:p-6 border-b flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Update SMS/Email Workflow</h3>
            <button
              onClick={onClose}
              data-testid="workflow-close-button"
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {saveError && <div role="alert" data-testid="workflow-update-error" className="mx-4 mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:mx-6">{saveError}</div>}
          <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Workflow Checkboxes */}
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.booking_confirmation_sms}
                    onChange={(e) => setWorkflowData({ ...workflowData, booking_confirmation_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">Booking Confirmation SMS</span>
                    
                    {pet.booking_confirmation_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.booking_confirmation_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.booking_confirmation_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.booking_confirmation_sms_date && getScheduledSMSForStep('booking_confirmation_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled: {new Date(getScheduledSMSForStep('booking_confirmation_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('booking_confirmation_sms').id, 'booking_confirmation_sms')}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.booking_confirmation_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'booking_confirmation_sms')}
                    className="ml-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.home_pickup_delivery_sms}
                    onChange={(e) => setWorkflowData({ ...workflowData, home_pickup_delivery_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">Home Pick up/Delivery SMS</span>
                    
                    {pet.home_pickup_delivery_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.home_pickup_delivery_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.home_pickup_delivery_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.home_pickup_delivery_sms_date && getScheduledSMSForStep('home_pickup_delivery_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled: {new Date(getScheduledSMSForStep('home_pickup_delivery_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('home_pickup_delivery_sms').id, 'home_pickup_delivery_sms')}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.home_pickup_delivery_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'home_pickup_delivery_sms')}
                    className="ml-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.euthanasia_support_sms}
                    onChange={(e) => setWorkflowData({ ...workflowData, euthanasia_support_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">Euthanasia Support SMS</span>
                    
                    {pet.euthanasia_support_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.euthanasia_support_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.euthanasia_support_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.euthanasia_support_sms_date && getScheduledSMSForStep('euthanasia_support_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled: {new Date(getScheduledSMSForStep('euthanasia_support_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('euthanasia_support_sms').id, 'euthanasia_support_sms')}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.euthanasia_support_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'euthanasia_support_sms')}
                    className="ml-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
            </div>



            {/* Pet Collected by Driver - NEW workflow step */}
            <div className="bg-teal-50 border border-teal-200 p-3 rounded-lg">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={workflowData.pet_collected_by_driver}
                  data-testid="workflow-driver-collected-checkbox"
                  onChange={(e) => setWorkflowData({ ...workflowData, pet_collected_by_driver: e.target.checked })}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 mr-3"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">🚐 Pet collected / received by driver</span>
                  
                  {pet.pet_collected_by_driver_date && (
                    <div className="text-xs text-teal-600 font-medium mt-1">
                      ✅ Collected: {parseUTCDatetime(pet.pet_collected_by_driver_date).toLocaleDateString('en-AU', { 
                        month: 'short', 
                        day: 'numeric' 
                      })} at {parseUTCDatetime(pet.pet_collected_by_driver_date).toLocaleTimeString('en-AU', { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      })}
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.pet_in_care_sms}
                    onChange={(e) => setWorkflowData({ ...workflowData, pet_in_care_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Pet in our Care SMS</span>
                    
                    {pet.pet_in_care_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.pet_in_care_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.pet_in_care_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.pet_in_care_sms_date && getScheduledSMSForStep('pet_in_care_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled for: {new Date(getScheduledSMSForStep('pet_in_care_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('pet_in_care_sms').id, 'pet_in_care_sms')}
                          className="text-red-600 hover:text-red-800 text-xs"
                          title="Cancel scheduled SMS"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.pet_in_care_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'pet_in_care_sms')}
                    className="ml-3 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.aquacremation_sms}
                    data-testid="workflow-cremation-checkbox"
                    onChange={(e) => setWorkflowData({ ...workflowData, aquacremation_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Cremation SMS</span>
                    
                    {pet.aquacremation_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.aquacremation_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.aquacremation_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.aquacremation_sms_date && getScheduledSMSForStep('aquacremation_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled: {new Date(getScheduledSMSForStep('aquacremation_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('aquacremation_sms').id, 'aquacremation_sms')}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.aquacremation_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'aquacremation_sms')}
                    className="ml-3 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
              {/* Cremation Date field hidden - using SMS sent date automatically */}
              {false && workflowData.aquacremation_sms && (
                <div className="ml-6">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cremation Date (for certificate)</label>
                  <input
                    type="datetime-local"
                    value={workflowData.aquacremation_sms_date}
                    onChange={(e) => setWorkflowData({ ...workflowData, aquacremation_sms_date: e.target.value })}
                    className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            <div className="bg-red-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.tree_planting_email}
                    onChange={(e) => setWorkflowData({ ...workflowData, tree_planting_email: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Tree Planting Email</span>
                    
                    {pet.tree_planting_email_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.tree_planting_email_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.tree_planting_email_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.tree_planting_email && openEmailModal && (
                  <button
                    type="button"
                    onClick={() => openEmailModal(pet.id, 'tree_planting_email')}
                    className="ml-3 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg"
                  >
                    📧 Send Email
                  </button>
                )}
              </div>
              {workflowData.tree_planting_email && (
                <div className="ml-6">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tree Planting Option</label>
                  <select
                    value={workflowData.tree_planting_option}
                    onChange={(e) => setWorkflowData({ ...workflowData, tree_planting_option: e.target.value })}
                    className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="OTP">OTP - One Tree Planted</option>
                    <option value="CN">CN - Carbon Neutral</option>
                  </select>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 p-3 rounded-lg" data-testid="tree-planting-reply-step">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.tree_planting_reply_email}
                    onChange={(e) => setWorkflowData({ ...workflowData, tree_planting_reply_email: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                    data-testid="tree-planting-reply-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Tree Planting Email Replies</span>
                    
                    {pet.tree_planting_reply_email_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        Sent: {parseUTCDatetime(pet.tree_planting_reply_email_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.tree_planting_reply_email_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.tree_planting_reply_email && openEmailModal && (
                  <button
                    type="button"
                    onClick={() => openEmailModal(pet.id, 'tree_planting_reply_email')}
                    className="ml-3 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg"
                    data-testid="tree-planting-reply-send-btn"
                  >
                    Send Email
                  </button>
                )}
              </div>
            </div>

            {/* All Other Emails — 5 ad-hoc email templates (certificate photo, pending wishes,
                afterpay, frame urn photo, paw impression photo). Opens the email modal which
                lets staff pick which of the 5 templates to send. */}
            <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/40">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">✉️ All Other Emails</span>
                    {(() => {
                      const sentCount = (pet.other_emails_sent_templates || []).length;
                      return (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          sentCount >= 5 ? 'bg-green-100 text-green-800' :
                          sentCount > 0 ? 'bg-amber-200 text-amber-900' :
                          'bg-gray-100 text-gray-600'
                        }`} data-testid="other-emails-badge">
                          {sentCount}/5 sent
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Certificate photo request · Pending wishes · AfterPay link · Photo requests
                  </p>
                  {pet.other_emails_last_sent_date && (
                    <div className="text-xs text-green-700 font-medium mt-1">
                      Last sent: {parseUTCDatetime(pet.other_emails_last_sent_date).toLocaleDateString('en-AU', {
                        month: 'short', day: 'numeric'
                      })} at {parseUTCDatetime(pet.other_emails_last_sent_date).toLocaleTimeString('en-AU', {
                        hour: 'numeric', minute: '2-digit', hour12: true
                      })}
                    </div>
                  )}
                </div>
                {openEmailModal && (
                  <button
                    type="button"
                    onClick={() => openEmailModal(pet.id, 'other_emails')}
                    className="ml-3 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                    data-testid="other-emails-send-btn"
                  >
                    ✉️ Send Email
                  </button>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.pickup_ashes_sms}
                    onChange={(e) => setWorkflowData({ ...workflowData, pickup_ashes_sms: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">Pick up Ashes SMS</span>
                    
                    {pet.pickup_ashes_sms_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.pickup_ashes_sms_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.pickup_ashes_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                    
                    {!pet.pickup_ashes_sms_date && getScheduledSMSForStep('pickup_ashes_sms') && (
                      <div className="text-xs text-orange-600 font-medium mt-1 flex items-center gap-2">
                        ⏰ Scheduled: {new Date(getScheduledSMSForStep('pickup_ashes_sms').scheduled_send_time + 'Z').toLocaleDateString('en-AU', { 
                          weekday: 'short',
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                        <button
                          onClick={() => cancelScheduledSMS(getScheduledSMSForStep('pickup_ashes_sms').id, 'pickup_ashes_sms')}
                          className="text-red-600 hover:text-red-800"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.pickup_ashes_sms && openSMSModal && (
                  <button
                    type="button"
                    onClick={() => openSMSModal(pet.id, 'pickup_ashes_sms')}
                    className="ml-3 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg"
                  >
                    📱 Send SMS
                  </button>
                )}
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={workflowData.ashes_collection_booked}
                  onChange={(e) => setWorkflowData({ ...workflowData, ashes_collection_booked: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                />
                <span className="text-sm text-gray-700">Ashes collection Booked</span>
                
              </label>
              {workflowData.ashes_collection_booked && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Collection Date & Time:</label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      value={workflowData.ashes_collection_datetime}
                      onChange={(e) => setWorkflowData({ ...workflowData, ashes_collection_datetime: e.target.value })}
                      className="text-sm px-2 py-1 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {/* Custom PS Icon */}
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold" style={{ fontSize: '8px' }}>PS</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <label className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={workflowData.certificate_email}
                    onChange={(e) => setWorkflowData({ ...workflowData, certificate_email: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Certificate Email</span>
                    
                    {pet.certificates_sent_date && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        ✅ Sent: {parseUTCDatetime(pet.certificates_sent_date).toLocaleDateString('en-AU', { 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {parseUTCDatetime(pet.certificates_sent_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </div>
                    )}
                  </div>
                </label>
                {!workflowData.certificate_email && openCertificateModal && (
                  <button
                    type="button"
                    onClick={() => openCertificateModal(pet.id)}
                    data-testid="workflow-preview-certificates-button"
                    disabled={!workflowData.aquacremation_sms}
                    className={`ml-3 px-3 py-1 text-white text-xs font-medium rounded-lg ${
                      workflowData.aquacremation_sms 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    title={!workflowData.aquacremation_sms ? 'Cremation must be completed first' : 'Preview and send certificates via email'}
                  >
                    📜 Send Certificates
                  </button>
                )}
              </div>
            </div>

            <ReviewRequestStep pet={pet} checked={workflowData.review_sms}
              onChange={value => setWorkflowData(prev => ({ ...prev, review_sms: value }))}
              onComplete={completeReview} openSMSModal={openSMSModal} openEmailModal={openEmailModal} />
          </div>

          </div>

          <div className="flex justify-end space-x-3 p-4 sm:px-6 border-t bg-white flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <button
              type="button"
              onClick={onClose}
              data-testid="workflow-cancel-button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="workflow-save-button"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Workflow'}
            </button>
          </div>
        </form>

        {/* Keepsake Confirmation Dialog - FOR LOCATION TRANSFERS */}
        {showKeepsakeConfirmation && keepsakeData && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl max-w-md w-full mx-4 p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ⚠️ Keepsakes Must Be Collected First
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This pet has selected keepsakes that must be collected before moving to cremation.
                </p>
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <p className="text-xs font-medium text-yellow-800 mb-2">Pet's Selected Keepsakes:</p>
                  <ul className="text-xs text-yellow-700">
                    {(keepsakeData.keepsakes || []).map((keepsake, index) => (
                      <li key={index}>• {keepsake}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleKeepsakeCancelled}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel Transfer
                  </button>
                  <button
                    onClick={handleKeepsakeConfirmed}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    ✅ Confirm: Keepsakes Collected
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Searchable Multi-Select Component
// Utility function to check if prepayment date is approaching
// Mobile-friendly multi-item selector component

export default WorkflowModal;
